import { DefaultError, useMutation } from "@tanstack/react-query";
import { PageModuleDataToCreate } from "entities/pages/types";
import { bulkUpdateModulesByBrandSetting } from "../api/bulkUpdateModulesByBrandSetting";
import { createSettingModule } from "../api/createSettingModule";
import {
  getModuleListByBranchSettingQueryData,
  invalidateModuleListByBrandSettingQuery,
} from "../queries/useModuleListByBrandSettingQuery";
import { invalidateSettingsListByBrandsQuery } from "../queries/useSettingsListByBrandQuery";

type Params = {
  data: PageModuleDataToCreate;
  brandId: string;
  settingId: string;
};

export const useCreateSettingModuleMutation = () => {
  return useMutation<void, DefaultError, Params>({
    mutationFn: async ({ data, settingId, brandId }) => {
      const prevData = getModuleListByBranchSettingQueryData(
        brandId,
        settingId,
      );

      const batchDataToUpdate =
        prevData?.reduce((acc, moduleData) => {
          if (moduleData.order < data.order) {
            return acc;
          }

          return {
            ...acc,
            [moduleData.id]: {
              order: moduleData.order + 1,
            },
          };
        }, {}) ?? {};

      const shouldBulkUpdate = !!Object.keys(batchDataToUpdate).length;

      await Promise.all([
        createSettingModule({
          brandId,
          settingId,
          moduleData: data,
        }),
        shouldBulkUpdate &&
          bulkUpdateModulesByBrandSetting(
            brandId,
            settingId,
            batchDataToUpdate,
          ),
      ]);
    },
    onSuccess: (_, { brandId, settingId }) => {
      invalidateModuleListByBrandSettingQuery(brandId, settingId);

      // invalidate query in 3 seconds
      setTimeout(() => {
        invalidateSettingsListByBrandsQuery(brandId);
      }, 3000);
    },
    onError: (e) => {
      console.error("Error occurred when creating module", e);
    },
  });
};
